import React, { useCallback } from 'react';
import {
  Loader2, AlertCircle, RefreshCw, Printer,
  TrendingUp, ListTodo, Rss,
} from 'lucide-react';
import { invalidateCache } from '../hooks/useNotionData';
import { updateTodoStatus } from '../lib/notion-api';
import { STATUS_COLOR } from '../data/agents';

// ── Sub-components ──
import { DonutChart } from './dashboard/DonutChart';
import { DailyBriefingCard, TodayMission } from './dashboard/BriefingSection';
import { RecentPosts, QuickTodos } from './dashboard/TodoSection';
import { KPICards, RevenueDonut } from './dashboard/KPISection';
import { NaverTrend } from './dashboard/TrendSection';
import { TokenUsage, AgentStatusBoard, AgentCostChart } from './dashboard/AgentSection';

export default function DashboardTab({ data, loading, error, onRefresh, onSilentRefresh, onOfficeTab }) {
  const handleTodoToggle = useCallback(async (pageId, newStatus) => {
    await updateTodoStatus(pageId, newStatus);
    invalidateCache();
    // 전체 로딩 화면 없이 백그라운드에서 조용히 새로고침
    if (onSilentRefresh) onSilentRefresh(); else onRefresh();
  }, [onRefresh, onSilentRefresh]);

  if (loading) {
    return (
      <div className="tab-content"><div className="n-loading-screen"><Loader2 size={28} className="n-spin" /><span>데이터 불러오는 중…</span></div></div>
    );
  }
  if (error) {
    return (
      <div className="tab-content"><div className="n-card" style={{ textAlign: 'center', padding: 32 }}>
        <AlertCircle size={24} style={{ color: 'var(--status-pending)', marginBottom: 8 }} />
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>연결 오류</div>
        <div style={{ fontSize: 12, color: 'var(--txt2)', marginBottom: 16 }}>{error}</div>
        <button onClick={onRefresh} className="n-btn-primary">다시 시도</button>
      </div></div>
    );
  }

  return (
    <article className="tab-content">
      <header className="n-page-header">
        <span>대시보드</span>
        <button
          className="n-print-btn"
          onClick={() => window.print()}
          title="대시보드 PDF 출력"
          aria-label="대시보드 PDF 출력"
        >
          <Printer size={12} aria-hidden="true" /> 리포트
        </button>
        <button className="n-icon-btn" onClick={onRefresh} aria-label="데이터 새로고침"><RefreshCw size={14} aria-hidden="true" /></button>
      </header>

      {/* ── 일일 브리핑 카드 (최상단) ── */}
      <section className="section" aria-label="일일 브리핑">
        <DailyBriefingCard data={data} />
      </section>

      {/* ── 오늘의 미션 + 오늘 할일 ── */}
      <section className="section" aria-label="오늘의 미션">
        <TodayMission data={data} />
        <QuickTodos data={data} onToggle={handleTodoToggle} onOfficeTab={onOfficeTab} />
      </section>

      {/* ── 비즈니스 KPI (추진감) ── */}
      <section className="section" aria-label="비즈니스 KPI">
        <div className="n-section-title"><TrendingUp size={12} /><span>비즈니스 KPI</span></div>
        <KPICards data={data} />
        <div className="n-charts-grid" style={{ marginBottom: 12 }}>
          <DonutChart data={data?.todos?.byStatus} title="할일 상태" colors={STATUS_COLOR} icon={ListTodo} />
          <RevenueDonut />
        </div>
      </section>

      {/* ── 에이전트 현황 보드 ── */}
      <section className="section" aria-label="에이전트 현황">
        <AgentStatusBoard data={data} />
      </section>

      {/* ── 트렌드 모니터링 (최우선) ── */}
      <section className="section" aria-label="트렌드 모니터링">
        <div className="n-section-title"><Rss size={12} /><span>트렌드 모니터링</span></div>
        <NaverTrend />
      </section>

      {/* ── 최근 포스트 + 토큰 ── */}
      <section className="section" aria-label="최근 포스트">
        <RecentPosts data={data} />
      </section>

      <section className="section" aria-label="AI 비용 추적">
        <TokenUsage />
        <AgentCostChart />
      </section>

      {data?.timestamp && <footer className="n-footer">마지막 업데이트: {new Date(data.timestamp).toLocaleString('ko-KR')}</footer>}
    </article>
  );
}
